import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import type { PublicOrgInfo } from "@/lib/backend/api";
import { logger } from "@/logger";

export interface OrganizationConfig {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  industry: string;
  website: string;
  founded: string;
  stats: {
    eventsPerYear?: string;
    flexWorkers?: string;
    regions?: string;
  };
  onboarding: {
    welcomeScreen: {
      title: string;
      subtitle: string;
      image?: string;
      icon: string;
    };
    slides: Array<{
      title: string;
      description: string;
      image?: string;
      icon: string;
      benefits?: string[];
      roles?: Array<{ name: string; description: string }>;
    }>;
    finalScreen: {
      title: string;
      subtitle: string;
      image?: string;
      ctaButtons: {
        primary: { text: string; action: string };
        secondary: { text: string; action: string };
      };
    };
  };
  colors: {
    primary: string;
    primaryDark?: string;
    secondary?: string;
    accent?: string;
    primaryContrast?: string;
  };
  regions?: string[];
}

// Helper function to map API response to OrganizationConfig
function mapPublicOrgInfoToConfig(orgInfo: PublicOrgInfo): OrganizationConfig | null {
  if (!orgInfo.onboarding?.enabled || !orgInfo.onboarding?.screens?.length) {
    return null;
  }

  const screens = orgInfo.onboarding.screens.sort((a, b) => a.order - b.order);

  // Extract welcome screen (first screen)
  const welcomeScreen = screens[0];

  // Extract feature/content slides (middle screens)
  const slides = screens.slice(1, -1).map((screen) => {
    const slide: OrganizationConfig["onboarding"]["slides"][0] = {
      title: screen.title || "",
      description: screen.subtitle || "",
      icon: screen.media?.type === "image" ? "🎯" : "✨", // Fallback icons
      image: screen.media?.url,
    };

    // Map list items to benefits
    if (screen.list && screen.list.length > 0) {
      slide.benefits = screen.list.map((item) => item.text);
    }

    // Map features to benefits
    if (screen.features && screen.features.length > 0) {
      slide.benefits = screen.features.map((feature) => feature.title);
    }

    return slide;
  });

  // Extract final screen (last screen)
  const finalScreen = screens[screens.length - 1];
  const primaryButton = finalScreen.buttons?.find((b) => b.variant === "primary");
  const secondaryButton = finalScreen.buttons?.find((b) => b.variant === "secondary");

  // Extract colors from theme or first screen
  const theme = orgInfo.onboarding.theme as any;
  const primaryColor = theme?.primaryColor || welcomeScreen.backgroundColor || "#6366f1";
  const primaryContrast = theme?.textColor || "#ffffff";

  return {
    slug: orgInfo.slug,
    name: orgInfo.name,
    tagline: welcomeScreen.subtitle || "",
    description: welcomeScreen.title || "",
    industry: "Flexible Staffing",
    website: orgInfo.startUrl || "",
    founded: new Date().getFullYear().toString(),
    stats: {
      flexWorkers: "500+",
      regions: "10+",
    },
    onboarding: {
      welcomeScreen: {
        title: welcomeScreen.title || "",
        subtitle: welcomeScreen.subtitle || "",
        image: welcomeScreen.media?.url,
        icon: "🎉",
      },
      slides,
      finalScreen: {
        title: finalScreen.title || "",
        subtitle: finalScreen.subtitle || "",
        image: finalScreen.media?.url,
        ctaButtons: {
          primary: {
            text: primaryButton?.label || "Start nu",
            action: primaryButton?.action || "register",
          },
          secondary: {
            text: secondaryButton?.label || "Inloggen",
            action: secondaryButton?.action || "signin",
          },
        },
      },
    },
    colors: {
      primary: primaryColor,
      primaryContrast: primaryContrast,
      primaryDark: theme?.primaryDark,
      secondary: theme?.secondary,
      accent: theme?.accent,
    },
    regions: [],
  };
}

/**
 * What an organization gets when it has configured no onboarding of its own.
 *
 * A function rather than a constant because the copy is translated now, and `t` only exists inside
 * a component or hook. It is genuinely a fallback — an organization that did configure onboarding
 * gets its own words from the api, in whatever language it wrote them.
 */
function buildDefaultConfig(t: (key: string) => string): OrganizationConfig {
  return {
    slug: "boomer",
    name: "Boomer",
    tagline: t("routes:onboarding.tagline"),
    description: t("routes:onboarding.intro"),
    industry: "Flexible Staffing",
    website: "https://heyboomer.nl",
    founded: "2020",
    stats: {
      flexWorkers: "5.000+",
      regions: "10+",
    },
    onboarding: {
      welcomeScreen: {
        title: t("routes:onboarding.welcome"),
        subtitle: t("routes:onboarding.discover"),
        icon: "💼",
      },
      slides: [
        {
          title: t("routes:onboarding.anywhere-title"),
          description: t("routes:onboarding.anywhere-body"),
          icon: "📅",
          benefits: [
            t("routes:onboarding.benefit-flexible"),
            t("routes:onboarding.benefit-areas"),
            t("routes:onboarding.benefit-fair-pay"),
          ],
        },
        {
          title: t("routes:onboarding.start-title"),
          description: t("routes:onboarding.start-body"),
          icon: "⚡",
          benefits: [
            t("routes:onboarding.quick-signup"),
            t("routes:onboarding.benefit-instant"),
            t("routes:onboarding.no-hidden-costs"),
          ],
        },
      ],
      finalScreen: {
        title: t("routes:onboarding.final-title"),
        subtitle: t("routes:onboarding.final-subtitle"),
        ctaButtons: {
          primary: { text: t("routes:onboarding.cta-start"), action: "register" },
          secondary: { text: t("routes:onboarding.cta-signin"), action: "signin" },
        },
      },
    },
    colors: {
      primary: "#6366f1",
      primaryDark: "#4f46e5",
      secondary: "#1e293b",
      primaryContrast: "#ffffff",
    },
  };
}

/**
 * Hook to check if onboarding is enabled Combines VITE_ENABLE_REGISTRATION env var with backend
 * onboarding config Returns true only if both registration is enabled AND backend has onboarding
 * data
 */
export function useOnboardingEnabled() {
  const { data: config, isLoading } = useOrganizationConfig();

  // While loading, assume onboarding is enabled to avoid flicker
  if (isLoading) {
    return { enabled: import.meta.env.VITE_ENABLE_REGISTRATION === "true", isLoading: true };
  }

  // Check both: env var must be true AND config must have onboarding data
  const enabled = import.meta.env.VITE_ENABLE_REGISTRATION === "true" && !!config?.onboarding;

  return { enabled, isLoading: false };
}

export function useOrganizationConfig() {
  const { t } = useTranslation();
  const organizationSlug = import.meta.env.VITE_ORGANIZATION_SLUG || "boomer";

  return useQuery<OrganizationConfig | null>({
    queryKey: ["organization-config", organizationSlug],
    queryFn: async () => {
      // If no slug or boomer, return default
      if (!organizationSlug || organizationSlug === "boomer") {
        return buildDefaultConfig(t);
      }

      try {
        // First, try to fetch from API
        logger.info(`[OrganizationConfig] Fetching from API for slug: ${organizationSlug}`);
        const response = await api.organizationPublic.getBySlug({ slug: organizationSlug });

        logger.info("[OrganizationConfig] API response:", response);

        if (response.data) {
          const mappedConfig = mapPublicOrgInfoToConfig(response.data);
          if (mappedConfig) {
            logger.info("[OrganizationConfig] ✅ Successfully loaded from API");
            logger.info("[OrganizationConfig] Config:", mappedConfig);
            return mappedConfig;
          }
          // If API succeeds but has no onboarding data, respect that - don't fallback
          logger.info(
            "[OrganizationConfig] ℹ️ API response has no onboarding configuration (disabled or not configured)",
          );
          return null;
        }
      } catch (apiError) {
        logger.warn(
          `[OrganizationConfig] ⚠️ API fetch failed for ${organizationSlug}, falling back to local config`,
          apiError,
        );
      }

      try {
        // Fallback to local JSON config if API fails
        // Use Vite's glob import to handle dynamic imports properly
        logger.info(`[OrganizationConfig] Loading local JSON for: ${organizationSlug}`);
        const configModules = import.meta.glob<{ default: OrganizationConfig }>(
          "../config/organizations/*.json",
          { eager: false },
        );
        const configPath = `../config/organizations/${organizationSlug}.json`;
        const configLoader = configModules[configPath];

        if (configLoader) {
          const config = await configLoader();
          logger.info("[OrganizationConfig] ✅ Successfully loaded from local JSON");
          logger.info("[OrganizationConfig] Config:", config.default);
          return config.default;
        } else {
          throw new Error(`No config found for ${organizationSlug}`);
        }
      } catch (error) {
        logger.warn(
          `[OrganizationConfig] ⚠️ No local config found for: ${organizationSlug}, using default`,
          error,
        );
        // Return default with organization name if custom config doesn't exist
        return {
          ...buildDefaultConfig(t),
          slug: organizationSlug,
          name:
            organizationSlug
              .split("-")
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ") || "Boomer",
        };
      }
    },
    staleTime: Infinity, // Config doesn't change during app runtime
  });
}
